// ════════════════════════════════════════════════════════════════════════
// AuthContext.tsx — Sesión de usuario con Context API
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: mismo patrón que ToastContext (Provider + custom hook).
//
// login() llama al backend real (POST /auth/login vía authService) — ver
// services/authService.ts. El JWT devuelto se guarda en `token`, junto al
// resto de la sesión.
//
// La sesión vive solo en memoria (useState) — se pierde al recargar la
// página. Es intencional: evita guardar el JWT en localStorage (superficie
// de ataque XSS) mientras no haya un manejo de sesión más robusto
// (ej. refresh token + almacenamiento httpOnly del lado del backend).
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser, LoginCredentials } from '../types/auth.types';
import { login as authLogin } from '@/infrastructure/http/authApiClient';

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>; // true = credenciales válidas
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      const result = await authLogin(credentials);
      setUser({ username: result.username, role: result.role });
      setToken(result.token);
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }, []);

  const logout = useCallback((): void => {
    setUser(null);
    setToken(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated: user !== null, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }

  return context;
}
